import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    FaBuilding, FaPhoneAlt, FaUserCircle, FaSignOutAlt,
    FaSignInAlt, FaBars, FaTimes
} from "react-icons/fa";
import Cookies from "js-cookie";
import { logoutUser } from "../../../services/authService";
import { useOnClickOutside } from "../../../hooks/pages/useOnClickOutside";

const navLinks = [
    { title: "Giới thiệu", path: "/about" },
    { title: "Dự án", path: "/projects" },
    { title: "Liên hệ", path: "/contact" },
    { title: "Tin tức", path: "/news" },
];

const buyMenuItems = [
    { title: "Mua - Bán", path: "/search?transactionType=sale" },
    { title: "Cho thuê", path: "/search?transactionType=rent" },
];

export default function Navbar() {
    const [showBuyMenu, setShowBuyMenu] = useState(false);
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navigate = useNavigate();
    const buyMenuRef = useRef(null);
    const accountMenuRef = useRef(null);

    useOnClickOutside(buyMenuRef, () => setShowBuyMenu(false));
    useOnClickOutside(accountMenuRef, () => setShowAccountMenu(false));

    useEffect(() => {
        const user = Cookies.get("user");
        setIsLoggedIn(!!user);
    }, []);

    const handleLogout = async () => {
        try { await logoutUser(); } catch {}
        Cookies.remove("user");
        Cookies.remove("accessToken");
        setIsLoggedIn(false);
        setMobileMenuOpen(false);
        navigate("/login");
    };

    const closeAllMenus = () => {
        setShowBuyMenu(false);
        setShowAccountMenu(false);
        setMobileMenuOpen(false);
    };

    return (
        <header className="sticky top-0 z-50 bg-[#082f49] backdrop-blur-lg shadow-xl border-b border-[#061f35]">
            <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                <Link
                    to="/"
                    className="flex items-center gap-2 font-bold tracking-widest uppercase text-white"
                    onClick={closeAllMenus}
                >
                    <div className="text-2xl font-serif font-bold tracking-widest uppercase">
                        <span className="text-[#c9a063]">Nhã Lan</span> An Phú
                    </div>
                </Link>

                <button
                    className="md:hidden text-2xl text-white"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    {mobileMenuOpen ? <FaTimes /> : <FaBars />}
                </button>

                <div className="hidden md:flex flex-1 items-center justify-between">
                    <nav className="flex mx-8 gap-6 font-medium text-white">
                        <div className="relative" ref={buyMenuRef}>
                            <button
                                onClick={() => setShowBuyMenu(!showBuyMenu)}
                                className="flex items-center gap-1"
                            >
                                Bất động sản ▾
                            </button>
                            {showBuyMenu && (
                                <div className="absolute bg-white text-blue-900 border rounded shadow-lg mt-2 z-50 min-w-[180px]">
                                    {buyMenuItems.map((item) => (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className="block px-4 py-2 hover:bg-[#0c2c4d] hover:text-white"
                                            onClick={() => setShowBuyMenu(false)}
                                        >
                                            {item.title}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>

                        {navLinks.map((link) => (
                            <Link key={link.path} to={link.path} className="hover:underline">
                                {link.title}
                            </Link>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4 text-white">
                        <Link
                            to="/post-car"
                            className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded flex items-center gap-2"
                        >
                            <FaBuilding /> ĐĂNG BĐS
                        </Link>
                        <div className="flex items-center gap-2 text-sm">
                            <FaPhoneAlt className="text-lg" />
                            <span>0848 989 339</span>
                        </div>
                        <div className="relative" ref={accountMenuRef}>
                            <button
                                onClick={() => setShowAccountMenu(!showAccountMenu)}
                                className="flex items-center gap-1"
                            >
                                <FaUserCircle className="text-xl" /> Tài khoản ▾
                            </button>
                            {showAccountMenu && (
                                <div className="absolute right-0 mt-2 bg-white text-blue-900 border rounded shadow-lg z-50 min-w-[180px]">
                                    {isLoggedIn ? (
                                        <>
                                            <Link to="/profile" className="block px-4 py-2 hover:bg-[#0c2c4d] hover:text-white" onClick={closeAllMenus}>
                                                Trang cá nhân
                                            </Link>
                                            <Link to="/my-properties" className="block px-4 py-2 hover:bg-[#0c2c4d] hover:text-white" onClick={closeAllMenus}>
                                                BĐS của tôi
                                            </Link>
                                            <button onClick={handleLogout} className="block w-full text-left px-4 py-2 hover:bg-[#0c2c4d] hover:text-white flex items-center gap-2">
                                                <FaSignOutAlt /> Đăng xuất
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <Link to="/login" className="flex items-center gap-2 px-4 py-2 hover:bg-[#0c2c4d] hover:text-white" onClick={closeAllMenus}>
                                                <FaSignInAlt /> Đăng nhập
                                            </Link>
                                            <Link to="/register" className="block px-4 py-2 hover:bg-[#0c2c4d] hover:text-white" onClick={closeAllMenus}>
                                                Đăng ký
                                            </Link>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MOBILE MENU */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-slate-900 text-white border-t p-4 space-y-2">
                    <div className="font-semibold text-slate-400">Bất động sản</div>
                    {buyMenuItems.map((item) => (
                        <Link key={item.path} to={item.path} className="block pl-4 py-2 rounded hover:bg-slate-700" onClick={closeAllMenus}>
                            {item.title}
                        </Link>
                    ))}

                    <div className="border-t my-2"></div>

                    {navLinks.map((link) => (
                        <Link key={link.path} to={link.path} className="block py-2 rounded hover:bg-slate-700" onClick={closeAllMenus}>
                            {link.title}
                        </Link>
                    ))}

                    <div className="border-t my-2"></div>

                    {isLoggedIn ? (
                        <>
                            <Link to="/profile" className="block py-2 rounded hover:bg-slate-700" onClick={closeAllMenus}>Trang cá nhân</Link>
                            <Link to="/my-properties" className="block py-2 rounded hover:bg-slate-700" onClick={closeAllMenus}>BĐS của tôi</Link>
                            <button onClick={handleLogout} className="w-full text-left py-2 rounded hover:bg-slate-700 flex items-center gap-2">
                                <FaSignOutAlt /> Đăng xuất
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="flex items-center gap-2 py-2 rounded hover:bg-slate-700" onClick={closeAllMenus}>
                                <FaSignInAlt /> Đăng nhập
                            </Link>
                            <Link to="/register" className="block py-2 rounded hover:bg-slate-700" onClick={closeAllMenus}>Đăng ký</Link>
                        </>
                    )}

                    <div className="border-t my-2"></div>

                    <Link to="/post-property" className="block bg-yellow-500 text-center py-3 rounded mt-4" onClick={closeAllMenus}>
                        ĐĂNG BĐS
                    </Link>
                </div>
            )}
        </header>
    );
}